import { useCallback, useEffect, useMemo, useState } from 'react';
import HomeIcon from '@mui/icons-material/Home';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import WebIcon from '@mui/icons-material/Web';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import VerifiedIcon from '@mui/icons-material/Verified';
import type { ChannelType } from '../CampaignDefinitionStep';

const ALL_STEPS = [
  { key: 'definition', label: 'Définition', Icon: HomeIcon },
  { key: 'creation', label: 'Création', Icon: BorderColorIcon },
  { key: 'creationRich', label: 'Rich SMS', Icon: WebIcon },
  { key: 'ciblage', label: 'Ciblage', Icon: TrackChangesIcon },
  { key: 'offers', label: 'Offre(s)', Icon: CardGiftcardIcon },
  { key: 'validation', label: 'Validation', Icon: VerifiedIcon },
] as const;

export type StepKey = (typeof ALL_STEPS)[number]['key'];
export type StepConfig = (typeof ALL_STEPS)[number];

interface StepVisibility {
  channel: ChannelType;
  hasOffer: boolean;
  isRichSms: boolean;
  noCreation: boolean;
}

const isStepVisible = (key: StepKey, { channel, hasOffer, isRichSms, noCreation }: StepVisibility) => {
  switch (key) {
    case 'creationRich': return isRichSms;
    case 'offers': return hasOffer && channel !== 'wallet';
    case 'creation': return channel !== 'courrier' && !noCreation;
    default: return true;
  }
};

const INITIAL_MOUNTED: ReadonlySet<StepKey> = new Set(['definition']);

export const useCampaignSteps = ({ channel, hasOffer, isRichSms, noCreation }: StepVisibility, resetKey: number) => {
  const [activeStep, setActiveStep] = useState(0);
  const [mounted, setMounted] = useState({ resetKey, keys: INITIAL_MOUNTED });
  const mountedSteps = mounted.resetKey === resetKey ? mounted.keys : INITIAL_MOUNTED;

  const visibleSteps = useMemo(
    () => ALL_STEPS.filter(step => isStepVisible(step.key, { channel, hasOffer, isRichSms, noCreation })),
    [channel, hasOffer, isRichSms, noCreation],
  );

  useEffect(() => {
    setActiveStep(step => Math.min(step, visibleSteps.length - 1));
  }, [visibleSteps.length]);

  const goToStep = useCallback((index: number) => {
    const key = visibleSteps[index]?.key;
    if (key) {
      setMounted(prev => ({ resetKey, keys: new Set(prev.resetKey === resetKey ? prev.keys : INITIAL_MOUNTED).add(key) }));
    }
    setActiveStep(index);
  }, [visibleSteps, resetKey]);

  return {
    visibleSteps,
    activeStep,
    activeKey: visibleSteps[activeStep]?.key,
    isLastStep: activeStep === visibleSteps.length - 1,
    isMounted: (key: StepKey) => mountedSteps.has(key),
    goToStep,
    goToKey: (key: StepKey) => goToStep(visibleSteps.findIndex(step => step.key === key)),
    hasStep: (key: StepKey) => visibleSteps.some(step => step.key === key),
  };
};
